import { defineStore } from 'pinia';
import { ref } from 'vue';
import { recordOriginsApi } from '../api/record-origins.api';
import { recordIndexersApi } from '../api/record-indexers.api';
import { recordTypesApi } from '../api/record-types.api';
import { recordProtectionMeansApi } from '../api/record-protection-means.api';
import { recordStagesApi } from '../api/record-stages.api';
import { createSettingsListStore } from './settings-list-store.factory';
import {
  exposeSettingsListState,
  createSettingsMutationState,
  createSettingsReadState,
} from './settings-list-state.factory';
import type { RecordOriginItem, RecordOriginPayload } from './settings.types';

export const useIndexingStore = createSettingsListStore('indexing-settings', recordIndexersApi);
export const useRecordTypeStore = createSettingsListStore('record-type-settings', recordTypesApi);
export const useProtectionMeansStore = createSettingsListStore(
  'protection-means-settings',
  recordProtectionMeansApi
);
export const useRecordStageStore = createSettingsListStore(
  'record-stage-settings',
  recordStagesApi
);

export const useRecordOriginStore = defineStore('record-origin-settings', () => {
  const read = createSettingsReadState<RecordOriginItem>(
    (query) => recordOriginsApi.list(query),
    'Failed to fetch record origins'
  );
  const mutation = createSettingsMutationState();
  const isCreating = ref(false);
  const isUpdating = ref(false);
  const isDeleting = ref(false);

  async function create(payload: RecordOriginPayload): Promise<boolean> {
    try {
      isCreating.value = true;
      mutation.reset();
      const created = await recordOriginsApi.create(payload);
      if (!created) return false;
    } catch (error: unknown) {
      mutation.record(error, 'Failed to create record origin');
      return false;
    } finally {
      isCreating.value = false;
    }

    // The row is committed, so a failure reading the collection back is a failure of the reload.
    await read.fetchAll();
    return true;
  }

  async function update(id: number, payload: RecordOriginPayload): Promise<boolean> {
    try {
      isUpdating.value = true;
      mutation.reset();
      const updated = await recordOriginsApi.update(id, payload);
      if (!updated) return false;
    } catch (error: unknown) {
      mutation.record(error, 'Failed to update record origin');
      return false;
    } finally {
      isUpdating.value = false;
    }

    // The renamed row may no longer belong to the query in force, and the listing decides that.
    await read.fetchAll();
    return true;
  }

  async function remove(id: number): Promise<boolean> {
    try {
      isDeleting.value = true;
      mutation.reset();
      const removed = await recordOriginsApi.remove(id);
      if (removed) read.items.value = read.items.value.filter((item) => item.id !== id);
      return removed;
    } catch (error: unknown) {
      mutation.record(error, 'Failed to delete record origin');
      return false;
    } finally {
      isDeleting.value = false;
    }
  }

  return {
    ...exposeSettingsListState(read, mutation, { isCreating, isUpdating, isDeleting }),
    create,
    update,
    remove,
  };
});
